(() => {
  if (customElements.get('headx-clinician-perspectives')) return;
  class HeadXClinicianPerspectives extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.viewport = this.querySelector('.hx-clinicians__viewport');
      this.track = this.querySelector('.hx-clinicians__track');
      this.cards = Array.from(this.track.children);
      this.filteredCards = this.cards.slice();
      this.filter = this.querySelector('.hx-clinicians__filter');
      this.productSelect = this.filter.querySelector('select');
      this.productSelect.value = 'all';
      this.footer = this.querySelector('.hx-clinicians__footer');
      this.allButton = this.querySelector('.hx-clinicians__all');
      this.pauseButton = this.querySelector('.hx-clinicians__pause');
      this.progress = this.querySelector('.hx-clinicians__progress span');
      this.counter = this.querySelector('.hx-clinicians__counter');
      this.announcement = this.querySelector('.hx-clinicians__announcement');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motion.matches || this.dataset.autoplay !== 'true' || Boolean(window.Shopify?.designMode);
      this.elapsed = 0;
      this.lastTick = performance.now();
      this.expanded = false;
      this.moving = false;
      this.swipeStart = null;
      this.suppressClickUntil = 0;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.querySelector('.hx-clinicians__previous').addEventListener('click', () => this.move(-1, true), options);
      this.querySelector('.hx-clinicians__next').addEventListener('click', () => this.move(1, true), options);
      this.pauseButton.addEventListener('click', () => { this.userPaused = !this.userPaused; this.syncPause(); }, options);
      this.allButton.addEventListener('click', () => this.toggleAll(), options);
      this.productSelect.addEventListener('change', () => this.applyFilter(), options);
      this.viewport.addEventListener('pointerdown', (event) => this.startSwipe(event), options);
      this.viewport.addEventListener('pointerup', (event) => this.endSwipe(event), options);
      this.viewport.addEventListener('pointercancel', () => { this.swipeStart = null; }, options);
      this.viewport.addEventListener('click', (event) => {
        if (performance.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, {signal: this.abort.signal, capture: true});
      window.addEventListener('resize', () => { if (this.moving) this.animation?.finish(); this.update(); }, options);
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.userPaused = true; this.syncPause(); }, options);
      this.addEventListener('shopify:block:select', async (event) => {
        const card = event.target.closest('.hx-clinicians__card');
        if (!card) return;
        if (card.hidden) {
          this.productSelect.value = 'all';
          await this.applyFilter();
        }
        if (this.moving && this.animation) {
          this.animation.finish();
          try { await this.animation.finished; } catch (_) {}
        }
        this.userPaused = true;
        const shown = Array.from(this.track.children).filter(item => !item.hidden);
        const selected = shown.indexOf(card);
        this.track.replaceChildren(...shown.slice(selected), ...shown.slice(0, selected), ...this.cards.filter(item => item.hidden));
        this.update();
      }, options);
      this.filter.hidden = this.dataset.showFilter === 'false';
      for (const option of this.productSelect.options) {
        if (option.value !== 'all') option.disabled = !this.cards.some(card => card.dataset.product === option.value);
      }
      this.classList.add('is-ready');
      this.update();
      this.timer = setInterval(() => this.tick(), 100);
    }
    get visibleCount() {
      return Math.min(this.filteredCards.length, parseInt(getComputedStyle(this).getPropertyValue('--hx-columns'), 10) || 1);
    }
    startSwipe(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) ||
          this.expanded || this.moving || this.filteredCards.length <= this.visibleCount ||
          event.target.closest('a, button, input, select, textarea')) return;
      this.swipeStart = {id: event.pointerId, x: event.clientX, y: event.clientY};
      this.viewport.setPointerCapture?.(event.pointerId);
    }
    endSwipe(event) {
      const start = this.swipeStart;
      this.swipeStart = null;
      if (!start || event.pointerId !== start.id) return;
      if (this.viewport.hasPointerCapture?.(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      this.suppressClickUntil = performance.now() + 450;
      this.move(dx < 0 ? 1 : -1, true);
    }
    update() {
      const count = this.visibleCount;
      const identities = this.filteredCards.map(card => card.querySelector('.hx-clinicians__identity'));
      identities.forEach(identity => { identity.style.minHeight = ''; });
      const identityHeight = Math.ceil(Math.max(0, ...identities.map(identity => identity.getBoundingClientRect().height)));
      identities.forEach(identity => { identity.style.minHeight = identityHeight + 'px'; });
      Array.from(this.track.children).forEach((card, index) => {
        const visible = !card.hidden && (this.expanded || index < count);
        card.inert = !visible;
        if (visible) card.removeAttribute('aria-hidden');
        else card.setAttribute('aria-hidden', 'true');
      });
      const overflow = this.filteredCards.length > count;
      this.dataset.matchedCount = String(this.filteredCards.length);
      this.footer.hidden = this.expanded || !overflow;
      this.allButton.hidden = !overflow && !this.expanded;
      const position = this.filteredCards.indexOf(this.track.firstElementChild) + 1;
      this.counter.textContent = this.dataset.counter.replace('[count]', position).replace('[total]', this.filteredCards.length);
      this.syncPause();
    }
    syncPause() {
      this.pauseButton.querySelector('[data-pause-label]').textContent = this.userPaused ? this.dataset.resume : this.dataset.pause;
      this.pauseButton.querySelector('[data-icon-pause]').hidden = this.userPaused;
      this.pauseButton.querySelector('[data-icon-play]').hidden = !this.userPaused;
      this.pauseButton.setAttribute('aria-label', this.userPaused ? this.dataset.resume : this.dataset.pause);
    }
    tick() {
      const now = performance.now();
      const delta = Math.min(now - this.lastTick, 500);
      this.lastTick = now;
      if (this.userPaused || document.hidden || this.expanded || this.moving || this.filteredCards.length <= this.visibleCount) return;
      this.elapsed += delta;
      const interval = Number(this.dataset.interval) || 25000;
      this.progress.style.transform = 'scaleX(' + Math.min(this.elapsed / interval, 1) + ')';
      if (this.elapsed >= interval) this.move(1, false);
    }
    async move(direction, manual) {
      if (this.moving || this.expanded || this.filteredCards.length <= this.visibleCount) return;
      this.moving = true;
      const width = this.track.firstElementChild.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = width + gap;
      if (direction < 0) this.track.prepend(Array.from(this.track.children).filter(card => !card.hidden).at(-1));
      const frames = direction > 0
        ? [{transform:'translateX(0)'}, {transform:'translateX(-' + distance + 'px)'}]
        : [{transform:'translateX(-' + distance + 'px)'}, {transform:'translateX(0)'}];
      this.animation = this.track.animate(frames, {duration:this.motion.matches ? 0 : 450, easing:'ease-in-out'});
      try { await this.animation.finished; } catch (_) {}
      if (!this.isConnected) return;
      if (direction > 0) this.track.insertBefore(this.track.firstElementChild, this.track.children[this.filteredCards.length] || null);
      this.animation.cancel();
      this.animation = null;
      this.elapsed = 0;
      this.progress.style.transform = 'scaleX(0)';
      this.moving = false;
      this.update();
      if (manual) {
        this.announcement.textContent = Array.from(this.track.children).slice(0,this.visibleCount).map(card => card.querySelector('.hx-clinicians__name').textContent).join(', ');
      }
    }
    async applyFilter() {
      // Keep the existing play or pause choice when the product filter changes.
      if (this.moving && this.animation) {
        this.animation.finish();
        try { await this.animation.finished; } catch (_) {}
      }
      const product = this.productSelect.value;
      this.filteredCards = this.cards.filter(card => product === 'all' || card.dataset.product === product);
      const excluded = this.cards.filter(card => !this.filteredCards.includes(card));
      this.cards.forEach(card => { card.hidden = !this.filteredCards.includes(card); });
      this.track.replaceChildren(...this.filteredCards, ...excluded);
      this.expanded = false;
      this.classList.remove('is-expanded');
      this.allButton.setAttribute('aria-expanded', 'false');
      this.allButton.querySelector('[data-all-label]').textContent = this.dataset.showAll;
      this.elapsed = 0;
      this.progress.style.transform = 'scaleX(0)';
      this.update();
      this.announcement.textContent = this.productSelect.selectedOptions[0].textContent + '. ' +
        this.dataset.filterResults.replace('[count]', this.filteredCards.length);
    }
    toggleAll() {
      if (this.moving) return;
      this.expanded = !this.expanded;
      if (this.expanded) this.track.replaceChildren(...this.filteredCards, ...this.cards.filter(card => !this.filteredCards.includes(card)));
      this.classList.toggle('is-expanded', this.expanded);
      this.allButton.setAttribute('aria-expanded', String(this.expanded));
      this.allButton.querySelector('[data-all-label]').textContent = this.expanded ? this.dataset.showCarousel : this.dataset.showAll;
      this.elapsed = 0;
      this.progress.style.transform = 'scaleX(0)';
      this.update();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.abort?.abort();
      this.animation?.cancel();
      this.ready = false;
    }
  }
  customElements.define('headx-clinician-perspectives', HeadXClinicianPerspectives);
})();
